import { useState } from "react";
import { toast } from "react-toastify";
import { FiDownload, FiUpload, FiCheck, FiRefreshCw } from "react-icons/fi";
import { Modal, Button, DataTable, Banner, StatTile } from "../common";
import {
  IMPORT_TYPES, downloadImportTemplate, uploadImportFile, getImportRows, downloadImportErrors, commitImport, cancelImport, getImportBatch,
} from "../../services/bulkImportApi";
import { downloadXlsx } from "../../utils/exportExcel"; // [NEW] used to build the employee Excel template in the browser
import { downloadCsv } from "../../utils/exportCsv"; // [NEW] used to build the employee CSV template in the browser
import { getErrorMessage, getErrorCode } from "../../utils/errors";

const PREVIEW_COLUMNS = {
  EMPLOYEE: [["employee_id", "ID"], ["employee_name", "Name"], ["official_email", "Email"], ["department", "Department"], ["designation", "Designation"], ["manager_employee_id", "Manager ID"], ["manager_name_source", "Manager name"], ["application_role", "Role"]],
  KRA_KPI: [["record_type", "Type"], ["code", "Code"], ["name", "Name"], ["parent_kra_code", "Parent KRA"], ["measurement_unit", "Unit"]],
  PERFORMANCE_CYCLE: [["cycle_name", "Cycle"], ["performance_start", "Starts"], ["performance_end", "Ends"], ["goal_end_at", "Goals close"], ["self_review_end_at", "Self-review closes"], ["manager_review_end_at", "Manager review closes"], ["hr_finalization_end_at", "HR closes"], ["rating_scheme_id", "Rating scheme"]],
};

// [NEW] Columns copied from "All Employees Master Data - Tracker.xlsx"
const EMPLOYEE_TEMPLATE_COLUMNS = [
  "Employee Number", "Full Name", "Work Email", "Date Of Birth", "Gender",
  "Nationality", "Mobile Phone", "Personal Email", "Current Address", "Location",
  "Legal Entity", "Business Unit", "Department", "Job Title", "Reporting To",
  "Reporting Manager Employee Number", "Date Joined", "Cost Center",
  "Professional Experience", "Primary Skill",
].map((label) => ({ key: label, label }));

export default function ImportWizardModal({ open, type = "EMPLOYEE", onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [batch, setBatch] = useState(null);
  const [invalid, setInvalid] = useState([]);
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null);
  const meta = IMPORT_TYPES[type] || IMPORT_TYPES.EMPLOYEE;

  const reset = () => { setFile(null); setBatch(null); setInvalid([]); setDone(null); };
  const close = () => { if (busy) return; reset(); onClose(); };

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(""); }
  };

  // [NEW] Excel template: employee import uses the tracker sheet columns; other types still use the server template.
  const downloadExcelTemplate = async () => {
    if (type === "EMPLOYEE") {
      downloadXlsx("employee-import-template", [
        { name: "Employees", title: "All Employees Master Data", columns: EMPLOYEE_TEMPLATE_COLUMNS, rows: [] },
      ]);
    } else {
      await downloadImportTemplate(type, "xlsx");
    }
  };

  // [NEW] CSV template: same employee columns as the Excel template.
  const downloadCsvTemplate = async () => {
    if (type === "EMPLOYEE") {
      downloadCsv("employee-import-template", EMPLOYEE_TEMPLATE_COLUMNS, []);
    } else {
      await downloadImportTemplate(type, "csv");
    }
  };

  const loadErrors = async (b) => {
    setInvalid(b.invalid ? (await getImportRows(b.id, "INVALID")).data : []);
  };

  const validate = () => run("validate", async () => {
    const { data } = await uploadImportFile(type, file);
    setBatch(data);
    await loadErrors(data);
  });

  const commit = () => run("commit", async () => {
    try {
      const { data } = await commitImport(batch.id);
      setDone(data);
      toast.success(`Imported ${data.total} ${meta.label}.`);
      onImported?.(data);
    } catch (err) {
      if (getErrorCode(err) === "IMPORT_REVALIDATION_FAILED") {
        const { data } = await getImportBatch(batch.id);
        setBatch(data);
        await loadErrors(data);
      }
      throw err;
    }
  });

  const discard = () => run("discard", async () => { await cancelImport(batch.id); reset(); });

  const previewColumns = (PREVIEW_COLUMNS[type] || []).map(([key, label]) => ({ key, label, render: (r) => r.data[key] || "—" }));

  const footer = done ? (
    <Button onClick={close}>Done</Button>
  ) : batch ? (
    <>
      <Button variant="ghost" onClick={discard} loading={busy === "discard"} disabled={Boolean(busy)}>Discard</Button>
      <Button variant="secondary" icon={FiRefreshCw} onClick={() => { setBatch(null); setInvalid([]); setFile(null); }} disabled={Boolean(busy)}>Upload another file</Button>
      <Button icon={FiCheck} onClick={commit} loading={busy === "commit"} disabled={Boolean(busy) || batch.status !== "VALIDATED"}>
        Import {batch.valid} row{batch.valid === 1 ? "" : "s"}
      </Button>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={close} disabled={Boolean(busy)}>Cancel</Button>
      <Button icon={FiUpload} onClick={validate} loading={busy === "validate"} disabled={!file}>Check file</Button>
    </>
  );

  return (
    <Modal
      open={open}
      size="lg"
      title={meta.title}
      subtitle="Upload a CSV or Excel file. The server checks every row first; nothing is saved until you confirm, and then either every row is imported or none."
      onClose={busy ? undefined : close}
      footer={footer}
    >
      <div className="form-stack">
        {!batch && !done && (
          <>
            <div className="import-drop">
              <input id={`import-file-${type}`} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="sr-only"
                onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
              <label htmlFor={`import-file-${type}`} className="btn btn-secondary"><FiUpload size={16} /> <span>Choose CSV or Excel file</span></label>
              <span className="muted">{file ? file.name : "No file chosen"}</span>
            </div>
            <div className="row-actions">
              {/* [UPDATED] Excel template now uses the tracker sheet columns for employees */}
              <Button variant="ghost" size="sm" icon={FiDownload} onClick={() => run("template", downloadExcelTemplate)}>Excel template</Button>
              <Button variant="ghost" size="sm" icon={FiDownload} onClick={() => run("template", downloadCsvTemplate)}>CSV template</Button>
            </div>
            <p className="form-hint">
              {type === "EMPLOYEE"
                ? "The Excel and CSV templates contain the employee master data columns. "
                : "The Excel template has an Instructions sheet (every column explained) and the current department and designation codes."}
              {type === "EMPLOYEE" && " Dates can be YYYY-MM-DD or DD-MM-YYYY. Only an administrator can import application roles."}
              {type === "KRA_KPI" && " A KPI row can name its parent KRA with parent_kra_code, including a KRA defined in the same file."}
            </p>
          </>
        )}

        {batch && !done && (
          <>
            <div className="stat-grid">
              <StatTile label="Rows checked" value={batch.total} />
              <StatTile label="Ready to import" value={batch.valid} tone="success" />
              <StatTile label="Rows with problems" value={batch.invalid} tone={batch.invalid ? "danger" : "success"} />
            </div>
            {batch.status === "VALIDATED" ? (
              <Banner tone="success" title="Every row passed the checks">Confirm to import all {batch.total} rows.</Banner>
            ) : (
              <Banner
                tone="warning"
                title="Fix these rows and upload the file again"
                action={<Button size="sm" variant="secondary" icon={FiDownload} onClick={() => run("errors", () => downloadImportErrors(batch.id, "xlsx"))}>Error report</Button>}
              >
                Nothing can be imported while any row has a problem. The error report lists each row with its original values and what to change.
              </Banner>
            )}
            {batch.ignoredColumns.length > 0 && (
              <p className="form-hint">Ignored columns (not part of the template): {batch.ignoredColumns.join(", ")}.</p>
            )}
            {type === "EMPLOYEE" && batch.summary?.manager_resolution && (
              <Banner
                tone={batch.summary.manager_resolution.unmatched?.length || batch.summary.manager_resolution.ambiguous?.length ? "warning" : "info"}
                title="Reporting manager name check"
              >
                {batch.summary.manager_resolution.unique_matches || 0} unique name match{batch.summary.manager_resolution.unique_matches === 1 ? "" : "es"} will be resolved after import.
                {batch.summary.manager_resolution.unmatched?.length > 0 && <> Unmatched: {batch.summary.manager_resolution.unmatched.map((x) => `${x.manager_name} (${x.employee_id})`).join(", ")}.</>}
                {batch.summary.manager_resolution.ambiguous?.length > 0 && <> Ambiguous: {batch.summary.manager_resolution.ambiguous.map((x) => `${x.manager_name} (${x.employee_id}; candidates ${x.candidate_employee_ids.join(", ")})`).join("; ")}.</>}
              </Banner>
            )}
            {invalid.length > 0 && (
              <DataTable keepOrder
                dense
                label="Rows with problems"
                rowKey="rowNo"
                pageSize={8}
                rows={invalid}
                columns={[
                  { key: "rowNo", label: "Row", width: 60 },
                  { key: "id", label: type === "EMPLOYEE" ? "Employee ID" : "Code", render: (r) => r.data.employee_id || r.data.code || "—" },
                  { key: "errors", label: "Problem", render: (r) => <span className="cell-stack">{r.errors.map((e) => <small key={e}>{e}</small>)}</span> },
                ]}
              />
            )}
            <p className="detail-label">Preview (first rows)</p>
            <DataTable keepOrder dense rowKey="rowNo" pageSize={5} rows={batch.preview} columns={[{ key: "rowNo", label: "Row", width: 60 }, ...previewColumns,
              { key: "status", label: "Check", render: (r) => <span className={`badge badge-${r.status === "VALID" ? "success" : "danger"}`}><span className="badge-dot" />{r.status === "VALID" ? "OK" : "Problem"}</span> }]} />
          </>
        )}

        {done && (
          <Banner tone="success" title={`${done.total} ${meta.label} imported`}>
            {type === "EMPLOYEE"
              ? `${(done.summary.created_employee_ids || []).length} employees created${done.summary.roles_granted ? `, ${done.summary.roles_granted} role assignments given` : ""}${(done.summary.resolved_reporting || []).length ? `, ${done.summary.resolved_reporting.length} manager names matched` : ""}. Send them a login invitation from the employee list or Users & roles.${(done.summary.unresolved_reporting || []).length ? ` ${done.summary.unresolved_reporting.length} manager name${done.summary.unresolved_reporting.length === 1 ? " needs" : "s need"} your review: use "Unmatched managers" on the Employees page.` : ""}`
              : `${(done.summary.kra_ids || []).length} KRAs and ${(done.summary.kpi_ids || []).length} KPIs added to the library.`}
          </Banner>
        )}
      </div>
    </Modal>
  );
}